import { formatClock } from "@/shared/utils/time"
import type { DashboardSession } from "./api/dashboard.queries"

// Arc ~300° open at the bottom: fraction 0 → 120° (sunrise, bottom-left),
// fraction 1 → 420°/60° (night start, bottom-right).
export const ARC = {
  width: 300,
  height: 290,
  cx: 150,
  cy: 150,
  radius: 120,
  strokeWidth: 24,
  startAngle: 120,
  sweepAngle: 300,
} as const

// 60s; refetch corrects drift.
export const SCORE_TICK_MS = 60_000

export const DEFAULT_DAY_START = "07:00"
export const DEFAULT_NIGHT_START = "19:30"

export function clamp01(t: number): number {
  return Math.min(1, Math.max(0, t))
}

// Fraction 0..1 → angle in degrees.
export function fractionToAngle(t: number): number {
  return ARC.startAngle + clamp01(t) * ARC.sweepAngle
}

// Point on a circle of the given radius, centered on the arc.
export function pointAtRadius(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180
  return { x: ARC.cx + radius * Math.cos(rad), y: ARC.cy + radius * Math.sin(rad) }
}

// Round to 2 decimals for compact SVG path data.
export function roundCoord(n: number): number {
  return Math.round(n * 100) / 100
}

export function arcPath(t0: number, t1: number): string {
  const a0 = fractionToAngle(t0)
  const a1 = fractionToAngle(t1)
  const p0 = pointAtRadius(a0, ARC.radius)
  const p1 = pointAtRadius(a1, ARC.radius)
  const largeArc = a1 - a0 > 180 ? 1 : 0
  return `M${roundCoord(p0.x)},${roundCoord(p0.y)} A${ARC.radius},${ARC.radius} 0 ${largeArc} 1 ${roundCoord(p1.x)},${roundCoord(p1.y)}`
}

// Instant → arc fraction, clamped so night-time instants collapse onto an endpoint.
export function timeToFraction(timeMs: number, dayStartMs: number, nightStartMs: number): number {
  if (nightStartMs <= dayStartMs) return 0
  return clamp01((timeMs - dayStartMs) / (nightStartMs - dayStartMs))
}

// "HH:mm" anchored to the calendar date of `anchor`. Null on bad input.
export function clockToDate(clock: string | null | undefined, anchor: Date): Date | null {
  if (!clock) return null
  const match = /^(\d{2}):(\d{2})$/.exec(clock)
  if (!match) return null
  const d = new Date(anchor)
  d.setHours(Number(match[1]), Number(match[2]), 0, 0)
  return d
}

// Same clock anchored to the day `offset` days from `anchor` (DST-safe: re-anchors
// the wall-clock rather than adding 24h, so the local hour is preserved).
function clockOnDay(clock: string, anchor: Date, offset: number): Date {
  const day = new Date(anchor)
  day.setDate(day.getDate() + offset)
  return clockToDate(clock, day) as Date
}

export type ArcMode = "day" | "night"

export interface ArcWindow {
  startMs: number // arc fraction 0
  endMs: number // arc fraction 1
  wakeMs: number // sunrise endpoint (morning wake)
  bedtimeMs: number // moon endpoint (bedtime)
  mode: ArcMode
}

// The arc window `now` falls in: the daytime wake→bedtime span, or (before dawn or
// after bedtime) the night bedtime→next-wake span. `wakeMs`/`bedtimeMs` name the
// endpoints semantically so the renderer never re-derives orientation from `mode`.
// Invalid/degenerate clocks fall back to the defaults for both endpoints.
export function resolveArcWindow(
  now: Date,
  dayStartClock: string | null | undefined,
  nightStartClock: string | null | undefined,
): ArcWindow {
  let dayClock = dayStartClock ?? DEFAULT_DAY_START
  let nightClock = nightStartClock ?? DEFAULT_NIGHT_START
  let day = clockToDate(dayClock, now)
  let night = clockToDate(nightClock, now)
  if (!day || !night || night <= day) {
    dayClock = DEFAULT_DAY_START
    nightClock = DEFAULT_NIGHT_START
    day = clockToDate(dayClock, now) as Date
    night = clockToDate(nightClock, now) as Date
  }
  const dayMs = (day as Date).getTime()
  const nightMs = (night as Date).getTime()

  if (now < (day as Date)) {
    // Pre-dawn: still last night's sleep.
    const bedtimeMs = clockOnDay(nightClock, now, -1).getTime()
    return { startMs: bedtimeMs, endMs: dayMs, wakeMs: dayMs, bedtimeMs, mode: "night" }
  }
  if (now >= (night as Date)) {
    // After bedtime: through to tomorrow morning.
    const wakeMs = clockOnDay(dayClock, now, 1).getTime()
    return { startMs: nightMs, endMs: wakeMs, wakeMs, bedtimeMs: nightMs, mode: "night" }
  }
  return { startMs: dayMs, endMs: nightMs, wakeMs: dayMs, bedtimeMs: nightMs, mode: "day" }
}

// Round caps (fill) and the capsule's rounded ends both bulge half a stroke-width
// past the endpoint. In arc-fraction units that overhang is this much; insetting a
// drawn segment by it lands its visible ends on the true times, so two sessions
// with a small real gap no longer overlap on the arc.
const CAP_FRACTION = ((ARC.strokeWidth / 2 / ARC.radius) * (180 / Math.PI)) / ARC.sweepAngle
// At/below this duration, show only the start time — start+end would overlap on a sliver.
export const SHORT_SLEEP_MAX_MIN = 10
// Min arc-fraction gap between a sleep's start/end labels; shorter segments fan out to it.
export const MIN_LABEL_GAP = 0.05
// Half the arc-width of a "HH:MM" label at the label radius. Start/end labels are
// inset from the segment's borders by this so they sit just inside the band
// rather than centred on (and spilling past) the endpoints.
export const LABEL_INSET = 0.022

// How far past `now` an ongoing session's dashed outline extends so its rounded
// end closes tightly around the now-marker (nesting the dot inside) rather than
// ending flush against it. Kept small so the dashed cap presses right onto the
// white dot: the cap's own semicircle (radius strokeWidth/2) already bulges a
// full marker-radius forward of its chord, so only a hair of extra reach past
// `now` is needed. Fixed arc-fraction → constant pixel offset for any window
// length.
export const NOW_WRAP_FRACTION = 0.002

// Leading end of an ongoing session's dashed outline: extended past `now` by the
// wrap amount, clamped to the window end so it never overflows the arc endpoint.
export function ongoingWrapEnd(t1: number): number {
  return Math.min(t1 + NOW_WRAP_FRACTION, 1)
}

// Inset a segment by the cap overhang so its rounded ends terminate at the true
// times; collapses to a midpoint dot when the session is shorter than the cap.
export function insetForCap(t0: number, t1: number): [number, number] {
  const a = t0 + CAP_FRACTION
  const b = t1 - CAP_FRACTION
  if (a < b) return [a, b]
  const mid = (t0 + t1) / 2
  return [mid, mid]
}

export interface ArcSegment {
  id: string
  type: DashboardSession["type"]
  session: DashboardSession
  t0: number
  t1: number
  startLabel: string | null
  endLabel: string | null
}

// Today's sessions → drawable arc segments. An ongoing session extends to now;
// zero-width segments (fully outside the day window) are dropped.
export function buildArcSegments(
  sessions: readonly DashboardSession[],
  dayStartMs: number,
  nightStartMs: number,
  nowMs: number,
): ArcSegment[] {
  return sessions
    .map((s) => {
      const startMs = new Date(s.startTime).getTime()
      const endMs = s.endTime ? new Date(s.endTime).getTime() : nowMs
      return {
        id: s.id,
        type: s.type,
        session: s,
        t0: timeToFraction(startMs, dayStartMs, nightStartMs),
        t1: timeToFraction(endMs, dayStartMs, nightStartMs),
        // A sleep spanning in from the previous window has its start clamped onto
        // fraction 0 — the real start is off-arc, so drop that label and keep the end.
        startLabel: startMs < dayStartMs ? null : formatClock(new Date(startMs)),
        // null on short sleeps, ongoing sessions, and sleeps spanning past the
        // window end (end clamped onto fraction 1 — off-arc, keep the start).
        endLabel:
          s.endTime && endMs <= nightStartMs && (endMs - startMs) / 60_000 > SHORT_SLEEP_MAX_MIN
            ? formatClock(new Date(endMs))
            : null,
      }
    })
    .filter((s) => s.t1 > s.t0)
}

// A single label sits on the segment centre. A start/end pair is inset just
// inside the borders (start after t0, end before t1) when the band is wide
// enough to keep them MIN_LABEL_GAP apart; otherwise the pair fans out
// symmetrically around the centre to that min gap, overflowing a short segment.
// The two branches meet exactly where width − 2·LABEL_INSET == MIN_LABEL_GAP.
export function sleepLabels(s: {
  t0: number
  t1: number
  startLabel: string | null
  endLabel: string | null
}): { fraction: number; text: string }[] {
  const mid = (s.t0 + s.t1) / 2
  // A single visible time (short sleep, ongoing, or spanning across a window
  // boundary) sits on the segment centre rather than crammed onto an endpoint.
  if (!s.startLabel && !s.endLabel) return []
  if (!s.endLabel) return [{ fraction: mid, text: s.startLabel as string }]
  if (!s.startLabel) return [{ fraction: mid, text: s.endLabel }]
  if (s.t1 - s.t0 - 2 * LABEL_INSET >= MIN_LABEL_GAP) {
    return [
      { fraction: s.t0 + LABEL_INSET, text: s.startLabel },
      { fraction: s.t1 - LABEL_INSET, text: s.endLabel },
    ]
  }
  const half = MIN_LABEL_GAP / 2
  return [
    { fraction: mid - half, text: s.startLabel },
    { fraction: mid + half, text: s.endLabel },
  ]
}

// Signed score as "±H h MM min".
export function formatScore(minutes: number): string {
  const sign = minutes < 0 ? "-" : "+"
  const abs = Math.abs(minutes)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `${sign}${h} h ${String(m).padStart(2, "0")} min`
}
